import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CREDIT_API_URL, TRUSTPAISA_URL, CASHE_URL } from '../../Constants.js';
import OtpVerificationModal from '../../utility/OtpVerificationModal';
import NotificationPopup from '../../utility/NotificationPopup';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import useApplicationStore from '../../store/applicationStore';
import getClientInfo from '../../utility/getClientInfo';

const HeroSection = ({ onScrollToForm }) => {
  const { t } = useTranslation('homepage');
  const navigate = useNavigate();
  const { setLeadData: setStoreLeadData, setCurrentStep: setStoreCurrentStep } = useApplicationStore();
  const utm = useApplicationStore(state => state.utm || {});
  const [loanFactorApiResponse, setLoanFactorApiResponse] = useState({
    minLoanAmount: 2500,
    maxLoanAmount: 300000,
  });
  const [loanAmount, setLoanAmount] = useState(50000);
  const [mobileNumber, setMobileNumber] = useState('');
  const [consent, setConsent] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const [loanAmountText, setLoanAmountText] = useState('50,000');
  const [isEditingLoanAmount, setIsEditingLoanAmount] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    mobileNumber: false,
    consent: false
  });
  const [loanAmountError, setLoanAmountError] = useState('');
  const [mobileNumberError, setMobileNumberError] = useState('');
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [leadData, setLeadData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success',
    redirectUrl: null,
    showLoader: false,
    lenderName: null,
    openInNewTab: false
  });
  const debounceTimeout = useRef(null);

  // Function to check eligibility and redirect
  const checkEligibilityAndRedirect = useCallback(async (currentLeadId, currentLoanAmount) => {
    try {
      const eligibilityResponse = await axios.get(`${CREDIT_API_URL}/eligibility/check?leadId=${currentLeadId}`);
      const eligibilityData = eligibilityResponse.data;
      console.log('Eligibility Check API Response:', eligibilityData);

      if (eligibilityData && eligibilityData.eligible) {
        // Update leadData in store with latest eligibility data
        setStoreLeadData({ ...(leadData || {}), eligibility: eligibilityData });
        setLeadData(prevLeadData => ({ ...prevLeadData, eligibility: eligibilityData })); // Also update local state

        const { lenderId, applicationStage, lenderLink, lenderName } = eligibilityData;

        if (lenderId === 'CASHE_001') {
          if (applicationStage === 'leadCreated' || applicationStage === 'personalDetailsFilled') {
            setStoreCurrentStep(1);
            setStoreLeadData({ ...(leadData || {}), eligibility: eligibilityData });
            navigate('/application/journey');
          } else if (applicationStage === 'preApprovalCheck') {
            setStoreLeadData({ ...(leadData || {}), eligibility: eligibilityData });
            setStoreCurrentStep(2);
            navigate('/application/journey');
          } else if (applicationStage === 'employmentDetailsFilled') {
            setStoreLeadData({ ...(leadData || {}), eligibility: eligibilityData });
            setStoreCurrentStep(2);
            navigate('/application/journey');
            // } else if (applicationStage === 'employmentDetailsFilled' || applicationStage === 'documentsUploaded') {
            //   setStoreLeadData({ ...(leadData || {}), eligibility: eligibilityData });
            //   setStoreCurrentStep(3);
            //   navigate('/application/journey');
          } else if (applicationStage === 'casheCustomerCreated') {
            setNotification({
              show: true,
              message: 'Your application is under progress. You can check latest status on our lending partner\'s website.',
              type: 'success',
              redirectUrl: CASHE_URL,
              showLoader: true,
              lenderName: 'cashe',
              openInNewTab: false
            });
          }
          else {
            setNotification({
              show: true,
              message: `Your application is under progress. You can check latest status on our lending partner's website.`,
              type: 'success',
              redirectUrl: CASHE_URL,
              showLoader: true,
              lenderName: 'cashe',
              openInNewTab: false
            });
          }
        } else if (lenderLink) {
          // For all other lenders (TrustPaisa, FIMoney, etc.), open in new tab
          setNotification({
            show: true,
            message: `Your application is under progress. You can check latest status on our lending partner's website.`,
            type: 'success',
            redirectUrl: lenderLink,
            showLoader: true,
            lenderName: lenderName?.toLowerCase().replace(/\s+/g, ''),
            openInNewTab: true
          });
        } else {
          setStoreLeadData({ ...(leadData || {}), eligibility: eligibilityData });
          navigate('/application/check-eligibility');
        }
      } else {
        // Not eligible, redirect to eligibility page
        setStoreLeadData({ ...(leadData || {}), eligibility: eligibilityData });
        navigate('/application/check-eligibility');
      }
    } catch (error) {
      console.error('Error checking eligibility:', error);
      setNotification({
        show: true,
        message: 'Failed to check eligibility. Please try again.',
        type: 'error',
        redirectUrl: null,
        showLoader: false,
        lenderName: null,
        openInNewTab: false
      });
    }
  }, [leadData, setStoreCurrentStep, setStoreLeadData, navigate, setNotification]);

  // Detect mobile screen to disable suggestion tooltips on mobile
  const [isMobile, setIsMobile] = useState(false);
  // Desktop suggestion tooltip visibility flags
  const [showLoanSuggestTip, setShowLoanSuggestTip] = useState(false);
  const [showDesktopMobileSuggestTip, setShowDesktopMobileSuggestTip] = useState(false);
  const loanSuggestTimerRef = useRef(null);
  const mobileSuggestTimerRef = useRef(null);
  useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.innerWidth <= 768);
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  // Fetching Loan Factor
  const fetchLoanFactor = useCallback(async () => {
    try {
      const payload = { productType: 'Personal Loan' }; // Default product type for homepage
      const response = await axios.post(`${CREDIT_API_URL}/fetch-loan-factor`, payload);
      const data = response.data.data;
      setLoanFactorApiResponse(data);
      // Initialize to 50,000 on page load, clamped to API range
      const desiredDefault = 50000;
      const clampedDefault = Math.min(
        Math.max(desiredDefault, data.minLoanAmount),
        data.maxLoanAmount
      );
      setLoanAmount(clampedDefault);
      setLoanAmountText(formatCurrency(clampedDefault));
    } catch (error) {
      console.error("Error fetching loan factor:", error.response ? error.response.data : error.message);
      // Fallback to default values if API fails
      setLoanFactorApiResponse({
        minLoanAmount: 5000,
        maxLoanAmount: 300000,
      });
      setLoanAmount(50000);
      setLoanAmountText(formatCurrency(50000));
    }
  }, []);

  useEffect(() => {
    fetchLoanFactor();
  }, [fetchLoanFactor]);

  // Debug: Monitor notification state changes
  useEffect(() => {
    if (notification.show) {
      console.log('=== HeroSection Notification State Changed ===');
      console.log('Full notification state:', notification);
      console.log('lenderName:', notification.lenderName);
      console.log('redirectUrl:', notification.redirectUrl);
      console.log('showLoader:', notification.showLoader);
      console.log('================================');
    }
  }, [notification]);

  const handleSliderChange = (e) => {
    const newAmount = parseInt(e.target.value);
    setLoanAmount(newAmount);
    setLoanAmountText(formatCurrency(newAmount));
    setLoanAmountError('');
    // Trigger animation by updating the key
    setAnimationKey(prev => prev + 1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const validateMobileNumber = (mobile) => {
    // Validate 10-digit Indian mobile number
    return /^[6-9]\d{9}$/.test(mobile);
  };

  const handleLoanAmountInput = (e) => {
    // Desktop suggestion tooltip: show for 2s when typing starts
    if (!isMobile) {
      setShowLoanSuggestTip(true);
      if (loanSuggestTimerRef.current) clearTimeout(loanSuggestTimerRef.current);
      loanSuggestTimerRef.current = setTimeout(() => setShowLoanSuggestTip(false), 2000);
    }
    setIsEditingLoanAmount(true);
    const value = e.target.innerText.trim().replace(/[,]/g, '').trim();
    const parsedValue = parseInt(value);
    const selection = window.getSelection();
    const range = selection?.getRangeAt(0);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      if (!isNaN(parsedValue)) {
        if (parsedValue >= loanFactorApiResponse.minLoanAmount && parsedValue <= loanFactorApiResponse.maxLoanAmount) {
          setLoanAmount(parsedValue);
          setLoanAmountText(formatCurrency(parsedValue));
          setAnimationKey(prev => prev + 1);
          setLoanAmountError(''); // Clear error

          // Move cursor to end after update
          setTimeout(() => {
            const element = e.target;
            if (element) {
              const range = document.createRange();
              const selection = window.getSelection();
              range.selectNodeContents(element);
              range.collapse(false); // false means collapse to end
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }, 0);
        } else {
          // Show error message for out of range values
          if (parsedValue < loanFactorApiResponse.minLoanAmount) {
            setLoanAmountError(`Please enter amount between ${formatCurrency(loanFactorApiResponse.minLoanAmount)} and ${formatCurrency(loanFactorApiResponse.maxLoanAmount)}`);
          } else if (parsedValue > loanFactorApiResponse.maxLoanAmount) {
            setLoanAmountError(`Please enter amount between ${formatCurrency(loanFactorApiResponse.minLoanAmount)} and ${formatCurrency(loanFactorApiResponse.maxLoanAmount)}`);
          }

          // Move cursor to end after showing error
          setTimeout(() => {
            const element = e.target;
            if (element) {
              const range = document.createRange();
              const selection = window.getSelection();
              range.selectNodeContents(element);
              range.collapse(false); // false means collapse to end
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }, 0);
        }
      } else {
        setLoanAmountText(formatCurrency(loanAmount));
        setLoanAmountError('Please enter a valid amount');

        // Move cursor to end after reverting
        setTimeout(() => {
          const element = e.target;
          if (element) {
            const range = document.createRange();
            const selection = window.getSelection();
            range.selectNodeContents(element);
            range.collapse(false); // false means collapse to end
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }, 0);
      }
    }, 300);
  };

  const handleStartApplication = async () => {
    const errors = {
      mobileNumber: !mobileNumber || !validateMobileNumber(mobileNumber),
      consent: !consent
    };

    setValidationErrors(errors);

    // If there are no errors, proceed with lead creation
    if (!errors.mobileNumber && !errors.consent) {
      setIsProcessing(true);
      try {
        // Create lead
        const leadPayload = {
          name: '', // Optional for homepage
          mobile: parseInt(mobileNumber),
          email: '', // Optional for homepage
          productType: 'Personal Loan',
          loanAmount: loanAmount,
          businessName: '', // Optional for homepage
          isConsentAccepted: consent
        };
        // Attach client/browser info
        try {
          const clientInfo = getClientInfo();
          leadPayload.browserName = clientInfo.browserName || null;
          leadPayload.deviceType = clientInfo.deviceType || null;

        } catch (e) {
          // ignore detection errors
        }
        // Include UTM tracking params if present
        if (utm) {
          leadPayload.utmSource = utm.utmSource || null;
          leadPayload.utmMedium = utm.utmMedium || null;
          leadPayload.utmCampaign = utm.utmCampaign || null;
        }

        const leadResponse = await axios.post(`${CREDIT_API_URL}/create-lead`, leadPayload);

        if (leadResponse.status === 200) {
          const leadDataFromResponse = leadResponse.data.data;
          setLeadData(leadDataFromResponse);
          setStoreLeadData(leadDataFromResponse); // Also set in Zustand store

          // Generate OTP
          const otpResponse = await axios.post(`${CREDIT_API_URL}/generate-otp`, {
            mobile: parseInt(mobileNumber),
            productType: 'Personal Loan'
          });

          if (otpResponse.status === 200) {
            setShowOtpModal(true);
          } else {
            throw new Error('OTP generation failed');
          }
        } else {
          throw new Error(leadResponse.data.message || 'Lead creation failed');
        }
      } catch (error) {
        console.error('Error in lead creation:', error);

        // Handle specific error case: Mobile not verified for this product type
        if (error.response?.status === 400 &&
          error.response?.data?.message === "Mobile is not verified for this product type. Please verify your mobile number first.") {

          // Set lead data from the error response
          if (error.response?.data?.data) {
            const leadDataFromError = error.response.data.data;
            setLeadData(leadDataFromError);
            setStoreLeadData(leadDataFromError); // Also set in Zustand store
          }

          // Generate OTP for verification
          try {
            const otpResponse = await axios.post(`${CREDIT_API_URL}/generate-otp`, {
              mobile: parseInt(mobileNumber),
              productType: 'Personal Loan'
            });

            if (otpResponse.status === 200) {
              setShowOtpModal(true);
            } else {
              throw new Error('OTP generation failed');
            }
          } catch (otpError) {
            console.error('Error generating OTP:', otpError);
            setNotification({
              show: true,
              message: 'Failed to send OTP. Please try again.',
              type: 'error',
              redirectUrl: null,
              showLoader: false,
              lenderName: null,
              openInNewTab: false
            });
          }
        } else if (error.response?.status === 409) {
          // Handle existing lead case
          const leadDataFromError = error.response?.data?.data;

          if (leadDataFromError) {
            // Store lead data
            setLeadData(leadDataFromError);
            setStoreLeadData(leadDataFromError);

            // Check if lead is Rejected - redirect to check-eligibility
            if (leadDataFromError.leadStatus === 'Rejected') {
              // For rejected leads, always go to check-eligibility page
              try {
                const otpResponse = await axios.post(`${CREDIT_API_URL}/generate-otp`, {
                  mobile: parseInt(mobileNumber),
                  productType: 'Personal Loan'
                });

                if (otpResponse.status === 200) {
                  setShowOtpModal(true);
                } else {
                  throw new Error('OTP generation failed');
                }
              } catch (otpError) {
                console.error('Error generating OTP:', otpError);
                setNotification({
                  show: true,
                  message: 'Failed to send OTP. Please try again.',
                  type: 'error',
                  redirectUrl: null,
                  showLoader: false,
                  lenderName: null,
                  openInNewTab: false
                });
              }
            } else {
              // For non-rejected leads, continue with normal flow (OTP then eligibility check)
              try {
                const otpResponse = await axios.post(`${CREDIT_API_URL}/generate-otp`, {
                  mobile: parseInt(mobileNumber),
                  productType: 'Personal Loan'
                });

                if (otpResponse.status === 200) {
                  setShowOtpModal(true);
                } else {
                  throw new Error('OTP generation failed');
                }
              } catch (otpError) {
                console.error('Error generating OTP:', otpError);
                setNotification({
                  show: true,
                  message: 'Failed to send OTP. Please try again.',
                  type: 'error',
                  redirectUrl: null,
                  showLoader: false,
                  lenderName: null,
                  openInNewTab: false
                });
              }
            }
          } else {
            // No lead data in error response, show error
            setNotification({
              show: true,
              message: error.response?.data?.message || 'Lead already exists but no data received.',
              type: 'error',
              redirectUrl: null,
              showLoader: false,
              lenderName: null,
              openInNewTab: false
            });
          }
        } else {
          // Handle other errors
          setNotification({
            show: true,
            message: error.response?.data?.message || error.message || 'Something went wrong. Please try again.',
            type: 'error',
            redirectUrl: null,
            showLoader: false,
            lenderName: null,
            openInNewTab: false
          });
        }
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleMobileNumberChange = (e) => {
    let value = e.target.value;
    // Desktop suggestion tooltip: show for 2s when typing starts
    if (!isMobile && value.length >= 0) {
      setShowDesktopMobileSuggestTip(true);
      if (mobileSuggestTimerRef.current) clearTimeout(mobileSuggestTimerRef.current);
      mobileSuggestTimerRef.current = setTimeout(() => setShowDesktopMobileSuggestTip(false), 2000);
    }

    // Remove any non-digit characters
    value = value.replace(/[^\d]/g, '');

    // Limit to 10 characters (10 digits)
    if (value.length > 10) {
      value = value.substring(0, 10);
    }

    setMobileNumber(value);

    // Real-time validation
    if (value) {
      if (validateMobileNumber(value)) {
        setMobileNumberError('');
        setValidationErrors(prev => ({ ...prev, mobileNumber: false }));
      } else {
        setMobileNumberError('Please enter a valid 10-digit mobile number');
        setValidationErrors(prev => ({ ...prev, mobileNumber: true }));
      }
    } else {
      setMobileNumberError('');
      setValidationErrors(prev => ({ ...prev, mobileNumber: false }));
    }
  };

  const handleConsentChange = (e) => {
    const checked = e.target.checked;
    setConsent(checked);
    // Clear validation error when user checks the box
    if (validationErrors.consent) {
      setValidationErrors(prev => ({ ...prev, consent: false }));
    }
  };

  const handleOtpSuccess = async () => {
    // Handle successful OTP verification
    if (leadData && leadData.id) {
      setShowOtpModal(false);

      // Check if lead is Rejected - redirect to check-eligibility page
      if (leadData.leadStatus === 'Rejected') {
        setStoreLeadData({ ...(leadData || {}), eligibility: leadData.eligibility });
        navigate('/application/check-eligibility');

        // Reset form fields
        setMobileNumber('');
        setConsent(false);
        setLeadData(null);
      } else {
        // For non-rejected leads, check eligibility and redirect normally
        await checkEligibilityAndRedirect(leadData.id, loanAmount);

        // Reset form fields after successful OTP and redirection logic
        setMobileNumber('');
        setConsent(false);
        setLeadData(null);
      }
    }
  };

  // Check if all required fields are filled and values are within allowed ranges
  const isFormValid = () => {
    const isLoanAmountInRange =
      typeof loanAmount === 'number' &&
      loanAmount >= (loanFactorApiResponse?.minLoanAmount ?? 0) &&
      loanAmount <= (loanFactorApiResponse?.maxLoanAmount ?? Number.MAX_SAFE_INTEGER);

    const isMobileValid = mobileNumber && validateMobileNumber(mobileNumber);
    const hasNoErrors = !loanAmountError && !mobileNumberError;

    return isLoanAmountInRange && isMobileValid && consent && hasNoErrors;
  };

  return (
    <section className="homepage-hero-section">
      <div className="homepage-hero-container">
        <div id="homepage-hero-card" className="homepage-hero-card">
          {/* Title */}
          <h1 className="homepage-hero-title">
            <span className="homepage-hero-title-primary">Get Instant Loan up to </span>
            <span className="homepage-hero-title-highlight">5 Lakh</span>
          </h1>

          {/* Loan Amount Input Field */}
          <div className="homepage-input-field-container">
            <OverlayTrigger
              placement="right"
              trigger="manual"
              show={!isMobile && showLoanSuggestTip}
              overlay={
                <Tooltip className="homepage-suggestion-tooltip">
                  Enter amount between {formatCurrency(loanFactorApiResponse.minLoanAmount)} and {formatCurrency(loanFactorApiResponse.maxLoanAmount)}
                </Tooltip>
              }
            >
              <div className={`homepage-input-field ${loanAmountError ? 'homepage-input-field-error' : ''}`}>
                <span className="homepage-input-prefix">₹</span>
                <div
                  contentEditable
                  suppressContentEditableWarning
                  onInput={handleLoanAmountInput}
                  onFocus={() => setIsEditingLoanAmount(true)}
                  onBlur={() => setIsEditingLoanAmount(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleLoanAmountInput(e);
                    }
                    // Prevent typing more than 10 characters
                    if (e.target.textContent && e.target.textContent.length >= 10 && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Tab') {
                      e.preventDefault();
                    }
                  }}
                  className="homepage-input-field-text homepage-content-editable-input"
                >
                  {loanAmountText}
                </div>
                <div className="homepage-loan-amount-placeholder">Enter Loan Amount</div>
              </div>
            </OverlayTrigger>
          </div>

          {/* Slider */}
          <div className="homepage-slider-container">
            <div className="homepage-slider-track">
              <input
                type="range"
                min={loanFactorApiResponse.minLoanAmount}
                max={loanFactorApiResponse.maxLoanAmount}
                step={1000}
                value={loanAmount}
                onChange={handleSliderChange}
                className="homepage-slider-input"
              />
              <div
                className="homepage-slider-thumb"
                style={{
                  left: `${((loanAmount - loanFactorApiResponse.minLoanAmount) / (loanFactorApiResponse.maxLoanAmount - loanFactorApiResponse.minLoanAmount)) * 100}%`
                }}
              >
                <span
                  key={animationKey}
                  className="homepage-slider-icon"
                >
                  <img src="/images/coin.png" alt="Slider Icon" />
                </span>
              </div>
            </div>
            <div className="homepage-slider-labels">
              <span className="homepage-slider-label">{formatCurrency(loanFactorApiResponse.minLoanAmount)}</span>
              <span className="homepage-slider-label">{formatCurrency(loanFactorApiResponse.maxLoanAmount)}</span>
            </div>
          </div>

          {/* Mobile Number Input Field */}
          <div className="homepage-input-field-container">
            <OverlayTrigger
              placement="right"
              trigger="manual"
              show={!isMobile && showDesktopMobileSuggestTip}
              overlay={
                <Tooltip className="homepage-suggestion-tooltip">
                  Enter 10 digit number starting with 6, 7, 8, or 9
                </Tooltip>
              }
            >
              <div className={`homepage-input-field ${mobileNumberError ? 'homepage-input-field-error' : ''}`}>
                <span className="homepage-input-prefix">+91</span>
                <input
                  type="tel"
                  placeholder=""
                  value={mobileNumber}
                  onChange={handleMobileNumberChange}
                  className="homepage-input-field-text"
                  maxLength="10"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <div className="homepage-hero-placeholder">Enter Your Mobile Number</div>
              </div>
            </OverlayTrigger>
          </div>

          {/* Consent Section */}
          <div className="homepage-consent-section">
            <div className="homepage-consent-content">
              <input
                type="checkbox"
                checked={consent}
                onChange={handleConsentChange}
                className="homepage-consent-checkbox"
              />
              <span className="homepage-consent-text">
                I allow Snapfunds to send an OTP for my mobile number verification.
                I also allow SnapFunds and its loan partner to share me details regarding the loan application process and available offers.
              </span>
            </div>
          </div>

          {/* Start Application Button */}
          {!isFormValid() && !isProcessing ? (
            <OverlayTrigger
              placement="bottom"
              trigger={['hover', 'click']}
              delay={{ show: 0, hide: 0 }}
              overlay={
                <Tooltip className="error-tooltip">
                  Please fill all required details to proceed
                </Tooltip>
              }
            >
              <span>
                <button
                  className="homepage-start-application-btn"
                  onClick={handleStartApplication}
                  disabled={isProcessing || !isFormValid()}
                >
                  {isProcessing ? 'Processing...' : 'Start Application'}
                </button>
              </span>
            </OverlayTrigger>
          ) : (
            <button
              className="homepage-start-application-btn"
              onClick={handleStartApplication}
              disabled={isProcessing || !isFormValid()}
            >
              {isProcessing ? 'Processing...' : 'Start Application'}
            </button>
          )}
        </div>
      </div>

      {/* OTP Verification Modal */}
      <OtpVerificationModal
        show={showOtpModal}
        mobile={mobileNumber}
        productType="Personal Loan"
        onClose={() => setShowOtpModal(false)}
        onVerified={handleOtpSuccess}
      />
      <NotificationPopup
        show={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({
          show: false,
          message: '',
          type: 'success',
          redirectUrl: null,
          showLoader: false,
          lenderName: null,
          openInNewTab: false
        })}
        redirectUrl={notification.redirectUrl}
        showLoader={notification.showLoader}
        lenderName={notification.lenderName}
        openInNewTab={notification.openInNewTab}
      />
    </section>
  );
};

export default HeroSection;